// Pull-to-refresh for the installed PWA (brief §2 follow-up: "pull down to force
// a sync"). In standalone mode there is no browser chrome to trigger a native
// refresh, so we implement the gesture ourselves: when the view is scrolled to
// the top and the user drags down past a threshold, releasing runs `onRefresh`.
//
// The indicator lives in the page's normal flow, at the very top of the content
// column: pulling grows its height, which pushes the page title ("Mon Cercle")
// down and reveals the spinner right above it. That keeps the spinner visually
// attached to the page rather than floating as a detached overlay.
//
// Touch-only by design — it attaches nothing meaningful on a mouse/desktop.

import { h } from './dom';

const THRESHOLD = 56; // px of (resisted) pull needed to arm a refresh
const MAX_PULL = 80; // clamp so it can't be dragged arbitrarily far
const RESISTANCE = 0.5; // finger travel → indicator travel, for a rubber feel
const MIN_SPIN_MS = 500; // keep the spinner up briefly so it doesn't just blink

/**
 * Wire pull-to-refresh onto a screen root (an app shell). The pull indicator is
 * inserted at the top of the shell's `.content` column so it pushes the title
 * down as it's revealed. `onRefresh` should resolve when the refresh is done.
 * Returns the same root element.
 */
export function withPullToRefresh(root: HTMLElement, onRefresh: () => Promise<void>): HTMLElement {
  // The content column is the thing that scrolls and holds the page title; the
  // indicator rides at its top so the two move together.
  const host = (root.querySelector('.content') as HTMLElement | null) ?? root;

  const spinner = h('span', { class: 'ptr-spinner', 'aria-hidden': 'true' });
  const label = h('span', { class: 'ptr-label small' });
  const indicator = h('div', { class: 'ptr-indicator', role: 'status', 'aria-live': 'polite' }, spinner, label);
  host.prepend(indicator);

  let startY = 0;
  let pulling = false;
  let armed = false;
  let refreshing = false;

  const setPull = (px: number): void => host.style.setProperty('--ptr', `${px}px`);

  const reset = (): void => {
    host.classList.remove('is-pulling');
    host.style.removeProperty('--ptr');
    pulling = false;
    armed = false;
  };

  const onStart = (e: TouchEvent): void => {
    if (refreshing || e.touches.length !== 1 || window.scrollY > 0) return;
    startY = e.touches[0]!.clientY;
    pulling = true;
    armed = false;
  };

  const onMove = (e: TouchEvent): void => {
    if (!pulling || refreshing) return;
    const dy = e.touches[0]!.clientY - startY;
    if (dy <= 0 || window.scrollY > 0) {
      reset();
      return;
    }
    e.preventDefault(); // take over from native scroll/overscroll while pulling
    host.classList.add('is-pulling');
    const pull = Math.min(dy * RESISTANCE, MAX_PULL);
    setPull(pull);
    armed = pull >= THRESHOLD;
    label.textContent = armed ? 'Relâcher pour synchroniser' : 'Tirer pour synchroniser';
  };

  const onEnd = (): void => {
    if (!pulling || refreshing || !armed) {
      reset();
      return;
    }
    refreshing = true;
    host.classList.remove('is-pulling');
    host.classList.add('is-refreshing');
    setPull(THRESHOLD);
    label.textContent = 'Synchronisation…';
    const started = Date.now();
    void Promise.resolve(onRefresh())
      .catch(() => {})
      .finally(() => {
        const wait = Math.max(0, MIN_SPIN_MS - (Date.now() - started));
        window.setTimeout(() => {
          refreshing = false;
          host.classList.remove('is-refreshing');
          reset();
        }, wait);
      });
  };

  root.addEventListener('touchstart', onStart, { passive: true });
  root.addEventListener('touchmove', onMove, { passive: false });
  root.addEventListener('touchend', onEnd);
  root.addEventListener('touchcancel', reset);

  return root;
}
